import React, { createRef } from "react";
import {
    Breadcrumb,
    Button,
    Col,
    Container,
    Form,
    Modal,
    Row,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faDownload,
    faEdit,
    faPlus,
    faTrashAlt,
} from "@fortawesome/free-solid-svg-icons";
import MultiSelect from "react-multi-select-component";

import MyTable from "components/MyTable";
import MySpinner from "components/MySpinner";
import ProcessingButton from "components/ProcessingButton";
import confirm from "components/Confirm";
import Toast from "services/Toast";
import ProductAttributeDictionaryService from "services/ProductAttributeDictionaryService";

const EMPTY_FORM = { id: null, ids: [], labels: "", values: "" };
const LOAD_ERROR_MESSAGE =
    "Something went wrong while loading the composite attributes dictionary";
const CELL_STYLE = { whiteSpace: "pre-wrap", wordBreak: "break-word" };

// A composite entry's `key` is the comma-joined, ASC-ordered list of unit IDs.
const buildKey = (options) =>
    options
        .map((opt) => opt.value)
        .sort((a, b) => a - b)
        .join(",");

class Composite extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            initialLoadFailed: false,
            dictionaryById: new Map(),
            dictionaryOptions: [],
            modalMode: null,
            processing: false,
            form: { ...EMPTY_FORM },
        };

        this.mounted = false;
        this.tableRef = createRef();
        this.readableKeyCache = new Map();

        this.handleAddOpen = this.handleAddOpen.bind(this);
        this.handleEditOpen = this.handleEditOpen.bind(this);
        this.handleModalClose = this.handleModalClose.bind(this);
        this.handleChangeIds = this.handleChangeIds.bind(this);
        this.handleChangeField = this.handleChangeField.bind(this);
        this.handleModalSubmit = this.handleModalSubmit.bind(this);
        this.handleDelete = this.handleDelete.bind(this);
        this.downloadSpreadsheet = this.downloadSpreadsheet.bind(this);

        this.tableRenderColumns = {
            control: {
                className: "text-center",
                format: (row) =>
                    row.saving ? (
                        <MySpinner />
                    ) : (
                        <>
                            <Button
                                size="sm"
                                className="tdButton mr-1"
                                variant="secondary"
                                onClick={this.handleEditOpen}
                                title="Edit composition"
                            >
                                <FontAwesomeIcon icon={faEdit} />
                            </Button>
                            <Button
                                size="sm"
                                className="tdButton"
                                variant="danger"
                                onClick={this.handleDelete}
                                title="Delete"
                            >
                                <FontAwesomeIcon icon={faTrashAlt} />
                            </Button>
                        </>
                    ),
            },
            key: {
                text: (row) => this.keyToReadable(row.key).toLowerCase(),
                format: (row) => this.keyToReadable(row.key),
                csv: (row) => this.keyToReadable(row.key),
            },
            labels: {
                text: (row) => (row.labels ?? "").toLowerCase(),
                format: (row) => <div style={CELL_STYLE}>{row.labels}</div>,
            },
            values: {
                text: (row) => (row.values ?? "").toLowerCase(),
                format: (row) => <div style={CELL_STYLE}>{row.values}</div>,
            },
        };
    }

    downloadSpreadsheet() {
        this.tableRef.current?.CSV();
    }

    componentDidMount() {
        this.mounted = true;
        Promise.all([
            ProductAttributeDictionaryService.units.all(),
            ProductAttributeDictionaryService.composites.all(),
        ])
            .then(([dictionary, composites]) => {
                if (!this.mounted) {
                    return;
                }
                if (dictionary === false || composites === false) {
                    this.handleLoadFailure();
                    return;
                }
                const dictionaryById = new Map();
                dictionary.forEach((entry) => {
                    dictionaryById.set(entry.id, entry);
                });
                const dictionaryOptions = dictionary
                    .map((entry) => ({ label: entry.key, value: entry.id }))
                    .sort((a, b) => a.label.localeCompare(b.label));
                this.setState({ dictionaryById, dictionaryOptions }, () => {
                    this.tableRef.current?.populate(composites);
                });
            })
            .catch(() => {
                if (!this.mounted) {
                    return;
                }
                this.handleLoadFailure();
            });
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    handleLoadFailure() {
        Toast.error(LOAD_ERROR_MESSAGE);
        this.setState({ initialLoadFailed: true }, () => {
            this.tableRef.current?.populate([]);
        });
    }

    getTableHeaders() {
        return [
            {
                field: "key",
                label: "KEY",
                sortable: true,
                searchable: true,
            },
            {
                field: "labels",
                label: "Known labels",
                sortable: true,
                searchable: true,
                width: "260px",
            },
            {
                field: "values",
                label: "Known values",
                sortable: true,
                searchable: true,
                width: "260px",
            },
            {
                field: "control",
                width: "90px",
                className: "text-center",
                content: (
                    <Button
                        size="sm"
                        className="tdButton"
                        variant="success"
                        onClick={this.handleAddOpen}
                        disabled={this.state.initialLoadFailed}
                        title="New composition"
                    >
                        <FontAwesomeIcon icon={faPlus} />
                    </Button>
                ),
            },
        ];
    }

    keyToReadable(key) {
        if (!key) {
            return "";
        }
        const cached = this.readableKeyCache.get(key);
        if (cached !== undefined) {
            return cached;
        }
        const readable = String(key)
            .split(",")
            .map((id) => {
                const entry = this.state.dictionaryById.get(Number(id));
                return entry?.key ?? `#${id}`;
            })
            .join(" + ");
        this.readableKeyCache.set(key, readable);
        return readable;
    }

    idsToOptions(key) {
        if (!key) {
            return [];
        }
        return String(key)
            .split(",")
            .map((id) => {
                const numeric = Number(id);
                const entry = this.state.dictionaryById.get(numeric);
                return {
                    label: entry?.key ?? `#${id}`,
                    value: numeric,
                };
            });
    }

    handleAddOpen() {
        this.setState({
            modalMode: "add",
            form: { ...EMPTY_FORM },
        });
    }

    handleEditOpen(evt) {
        evt.stopPropagation();
        evt.preventDefault();
        const match = this.tableRef.current?.getRowFromEvent(evt);
        const row = match?.row;
        if (!row) {
            return;
        }
        this.setState({
            modalMode: "edit",
            form: {
                id: row.id,
                ids: this.idsToOptions(row.key),
                labels: row.labels ?? "",
                values: row.values ?? "",
            },
        });
    }

    handleModalClose() {
        if (this.state.processing) {
            return;
        }
        this.setState({ modalMode: null });
    }

    handleChangeIds(selected) {
        this.setState((prev) => ({ form: { ...prev.form, ids: selected } }));
    }

    handleChangeField(evt) {
        const field = evt.target.getAttribute("name");
        const value = evt.target.value;
        this.setState((prev) => ({
            form: { ...prev.form, [field]: value },
        }));
    }

    canSubmit() {
        const { modalMode, form } = this.state;
        if (form.ids.length < 2) {
            return false;
        }
        if (modalMode === "add") {
            return (
                form.labels.trim().length > 0 && form.values.trim().length > 0
            );
        }
        return true;
    }

    handleModalSubmit() {
        if (this.state.modalMode === "add") {
            this.submitAdd();
            return;
        }
        this.submitEdit();
    }

    submitAdd() {
        const { form } = this.state;
        const payload = {
            key: buildKey(form.ids),
            labels: form.labels.trim(),
            values: form.values.trim(),
        };
        const readable = form.ids.map((opt) => opt.label).join(" + ");
        this.setState({ processing: true });
        ProductAttributeDictionaryService.composites
            .create(payload)
            .then((row) => {
                if (!this.mounted) {
                    return;
                }
                if (!row || !row.id) {
                    Toast.error(
                        row?.message ||
                            "Failed to create composition. The combination may already exist."
                    );
                    this.setState({ processing: false });
                    return;
                }
                this.setState({ processing: false, modalMode: null }, () => {
                    this.tableRef.current?.addRow(row);
                    Toast.ok(`Composition "${readable}" added`);
                });
            })
            .catch(() => {
                if (!this.mounted) {
                    return;
                }
                Toast.error("Failed to create composition.");
                this.setState({ processing: false });
            });
    }

    submitEdit() {
        const { form } = this.state;
        const match = this.tableRef.current?.getRowByID(form.id);
        if (!match?.row) {
            this.setState({ modalMode: null });
            return;
        }
        const newKey = buildKey(form.ids);
        const labels = form.labels.trim();
        const values = form.values.trim();
        const payload = {};
        if (newKey !== (match.row.key ?? "")) {
            payload.key = newKey;
        }
        if (labels !== (match.row.labels ?? "")) {
            payload.labels = labels;
        }
        if (values !== (match.row.values ?? "")) {
            payload.values = values;
        }
        if (Object.keys(payload).length === 0) {
            this.setState({ modalMode: null });
            return;
        }
        this.setState({ processing: true });
        ProductAttributeDictionaryService.composites
            .save(form.id, payload)
            .then((ok) => {
                if (!this.mounted) {
                    return;
                }
                if (!ok) {
                    Toast.error(
                        "Failed to update composition. The combination may already exist."
                    );
                    this.setState({ processing: false });
                    return;
                }
                this.tableRef.current?.updateRow({
                    ...match.row,
                    ...payload,
                });
                this.setState({ processing: false, modalMode: null }, () => {
                    Toast.ok("Composition updated");
                });
            })
            .catch(() => {
                if (!this.mounted) {
                    return;
                }
                Toast.error("Failed to update composition.");
                this.setState({ processing: false });
            });
    }

    async handleDelete(evt) {
        evt.stopPropagation();
        evt.preventDefault();
        const match = this.tableRef.current?.getRowFromEvent(evt);
        const row = match?.row;
        if (!row) {
            return;
        }
        const readable = this.keyToReadable(row.key);
        const confirmed = await confirm("Are you sure?", "Yes", "Cancel", {
            message: `You're about to remove the composition "${readable}". This operation can't be undone. Would you like to proceed?`,
        });
        if (!confirmed) {
            return;
        }
        this.tableRef.current?.updateRow({ ...row, saving: true });
        ProductAttributeDictionaryService.composites
            .delete(row.id)
            .then((result) => {
                if (!this.mounted) {
                    return;
                }
                if (result === true) {
                    this.tableRef.current?.deleteRow(row);
                    Toast.ok(`Composition "${readable}" removed`);
                    return;
                }
                Toast.error(
                    result?.message || "Failed to delete this composition"
                );
                this.tableRef.current?.updateRow(row);
            })
            .catch(() => {
                if (!this.mounted) {
                    return;
                }
                Toast.error("Failed to delete this composition");
                this.tableRef.current?.updateRow(row);
            });
    }

    renderModal() {
        const { modalMode, processing, form, dictionaryOptions } = this.state;
        const isAdd = modalMode === "add";
        const isEdit = modalMode === "edit";
        return (
            <Modal
                show={isAdd || isEdit}
                onHide={this.handleModalClose}
                backdrop="static"
                keyboard={!processing}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {isAdd ? "New composition" : "Edit composition"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row className="mb-2">
                        <Col>
                            <Form.Label>
                                Dictionary entries (pick at least two)
                            </Form.Label>
                            <MultiSelect
                                className="selector"
                                hasSelectAll={false}
                                options={dictionaryOptions}
                                value={form.ids}
                                onChange={this.handleChangeIds}
                                labelledBy="Select dictionary entries"
                                disabled={processing}
                            />
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col>
                            <Form.Label>Known labels</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                name="labels"
                                value={form.labels}
                                onChange={this.handleChangeField}
                                readOnly={processing}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Label>Known values</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                name="values"
                                value={form.values}
                                onChange={this.handleChangeField}
                                readOnly={processing}
                            />
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer style={{ justifyContent: "center" }}>
                    <Button
                        size="sm"
                        variant="secondary"
                        onClick={this.handleModalClose}
                        disabled={processing}
                    >
                        Cancel
                    </Button>
                    <ProcessingButton
                        size="sm"
                        disabled={!this.canSubmit()}
                        processing={processing}
                        processingLabel={isAdd ? "Adding ..." : "Saving ..."}
                        label={isAdd ? "Add" : "Save"}
                        onClick={this.handleModalSubmit}
                    />
                </Modal.Footer>
            </Modal>
        );
    }

    render() {
        return (
            <Container className="pt-4">
                <span style={{ float: "right" }}>
                    <Button
                        disabled={this.state.initialLoadFailed}
                        size="sm"
                        className="tdButton"
                        variant="success"
                        onClick={this.downloadSpreadsheet}
                    >
                        <FontAwesomeIcon icon={faDownload} /> Download
                    </Button>
                </span>
                <Breadcrumb className="dd">
                    <Breadcrumb.Item>Others</Breadcrumb.Item>
                    <Breadcrumb.Item>Attributes Dictionary</Breadcrumb.Item>
                    <Breadcrumb.Item active>Composite Entries</Breadcrumb.Item>
                </Breadcrumb>

                {this.renderModal()}

                <MyTable
                    name="composite-attributes-dictionary"
                    ref={this.tableRef}
                    dynamic={false}
                    sortBy="key"
                    sortOrder="asc"
                    headers={this.getTableHeaders()}
                    renderColumns={this.tableRenderColumns}
                    emptyLabel="No compositions"
                />
            </Container>
        );
    }
}

export default Composite;
